﻿import { LiveDataSet } from "../../API/LiveData/LiveDataSet";
import { IUnitsConversionSet } from "../../Converters/UnitsConverter/IUnitsConverter";
import { ILiveDataListener } from "../../ILiveDataListener";
import { InfluxDataWrapper } from "../../InfluxDb/InfluxDataWrapper";
import { getGroupData } from "../../InfluxDb/InfluxDb";
import { MainSite } from "../../MainSite";
import { ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { ISiteObservationsPage } from "./ISiteObservationsPage";

export class CloudChart implements ILiveDataListener {

    private chartContainer: JQuery;
    private chart: __Highcharts.ChartObject;
    private stationId: string;
    private timeZoneString: string;

    constructor(private mainSite: MainSite, page: ISiteObservationsPage, container: JQuery, top: number, left: number, height: number, width: number) {

        this.stationId = page.station.Station.Id;

        var box: JQuery = $('<div class="midi_SiteObservations_chart_container"></div>')
            .css('position', 'absolute')
            .css('top', top + '%')
            .css('left', left + '%')
            .css('height', height + '%')
            .css('width', width + '%');

        container.append(box);

        this.chartContainer = $('<div></div>')
            .css('height', '100%')
            .appendTo(box);

        this.timeZoneString = this.mainSite.getTimeZoneString();


        this.drawChart();
        this.updateChart();

    }

    public liveDataChanged(latestData: LiveDataSet): void {
        this.updateChart();
    }

    private drawChart(): void {
        var yAxisOptions = [];

        yAxisOptions.push(
            {
                title: {
                    text: `Height (${MainSite.getUnitConverter().getConvertedUnits(ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Units, ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Id)})`
                },
                min: 0,
                lineColor: '#666',
                //lineWidth: 1,
                gridLineColor: '#666',
                gridLineWidth: 0.5,
                gridLineDashStyle: 'longdash'
            },
            {
                title: {
                    text: `Visibility (${MainSite.getUnitConverter().getConvertedUnits(ParameterGroupInfoSet.Visibility_m.Units, ParameterGroupInfoSet.Visibility_m.Id)})`
                },
                min: 0,
                opposite: true,
                lineColor: '#666',
                //lineWidth: 1,
                gridLineColor: '#666',
                gridLineWidth: 0.5,
                gridLineDashStyle: 'longdash'
            });


        this.chartContainer.highcharts(<any>{
            chart: {
                type: 'line',
                backgroundColor: '#FFF',
                marginTop: 30
            },
            exporting: {
                enabled: false
            },
            title: {
                text: ''
            },
            xAxis: {
                type: 'datetime',
                ordinal: false,
                lineWidth: 0,
                lineColor: '#666'
            },
            yAxis: yAxisOptions,
            legend: {
                enabled: true
            },
            plotOptions: {
                series: {
                    animation: false,
                    events: {
                        legendItemClick: function () {
                            return false;
                        }
                    }
                }
            },
            credits: {
                enabled: false
            },
            tooltip: {
                xDateFormat: '%Y-%m-%d %H:%M:%S ' + this.timeZoneString,
                valueDecimals: 0
            }
        });

        this.chart = this.chartContainer.highcharts();
        this.chart.showLoading('Getting data...');
    }

    private updateChart(): void {
        if (!this.mainSite.liveDataManager.latestData) return;

        var seriesOptions: __Highcharts.SeriesOptions[] = [];

        var instCloudGroupIds: string[] = [
            ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Id,
            ParameterGroupInfoSet.Second_CloudBaseHeight_ft_Inst.Id,
            ParameterGroupInfoSet.Third_CloudBaseHeight_ft_Inst.Id
        ];

        var toMoment = moment.utc();
        var fromMoment = moment.utc().subtract(3, 'h'); // 2 hours ago

        var anyData = false;

        instCloudGroupIds.forEach((paramGroupId: string) => {
            getGroupData(this.stationId, paramGroupId, fromMoment.valueOf(), toMoment.valueOf(), (err: any, data: InfluxDataWrapper) => {
                if (err) {
                    //alert('Error getting data: ' + err);
                    console.warn('Error getting data (CloudChart - instCloudGroupIds)', err);
                    this.chart.showLoading('Error getting data' + (typeof (err) == 'string' ? `: ${err}` : ''));
                    return;
                }

                anyData = anyData || data.Data !== null;

                var showInLegend: boolean = data.ParameterId == ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Id;

                let unitsConversionSet: IUnitsConversionSet | null = MainSite.getUnitConverter().getConversionSet(ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Units, ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Id);

                if (data.Data !== null) {
                    if (unitsConversionSet) {
                        data.Data = data.Data.map(x => {
                            if (Array.isArray(x) && x.length == 2) {
                                return [x[0], unitsConversionSet.convertValue(x[1])];
                            }

                            return x;
                        });
                    }

                    data.Data.unshift([fromMoment.valueOf(), null]);
                    data.Data.push([toMoment.valueOf(), null]);
                }

                seriesOptions.push({
                    id: paramGroupId,
                    name: `Instantaneous Cloud (${MainSite.getUnitConverter().getConvertedUnits(ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Units, ParameterGroupInfoSet.First_CloudBaseHeight_ft_Inst.Id)})`,
                    data: data.Data,
                    lineWidth: 0,
                    marker: {
                        enabled: true,
                        radius: 2,
                        symbol: 'circle'
                    },
                    yAxis: 0,
                    color: '#666',
                    showInLegend: showInLegend,
                });

                if (seriesOptions.length == 4) {
                    if (anyData) {
                        this.replaceChartSeries(seriesOptions);
                    } else {
                        this.chart.showLoading('Data not available');
                    }
                }
            });
        });

        getGroupData(this.stationId, "Visibility_m", fromMoment.valueOf(), toMoment.valueOf(), (err: any, data: InfluxDataWrapper) => {
            if (err) {
                // alert('Error getting data: ' + err);
                console.warn('Error getting data (CloudChart - visibility)', err);
                this.chart.showLoading('Error getting data' + (typeof (err) == 'string' ? `: ${err}` : ''));
                return;
            }

            anyData = anyData || data.Data !== null;

            let unitsConversionSet: IUnitsConversionSet | null = MainSite.getUnitConverter().getConversionSet(ParameterGroupInfoSet.Visibility_m.Units, ParameterGroupInfoSet.Visibility_m.Id);

            if (data.Data !== null) {
                /*data.Data = data.Data.map(x => {
                    if (Array.isArray(x) && x.length == 2) {
                        return [ x[0], MainSite.getUnitConverter().convert(x[1], ParameterGroupInfoSet.Visibility_m.Units, ParameterGroupInfoSet.Visibility_m) ];
                    }

                    return x;
                });
                */
                if (unitsConversionSet) {
                    data.Data = data.Data.map(x => {
                        if (Array.isArray(x) && x.length == 2) {
                            return [x[0], unitsConversionSet.convertValue(x[1])];
                        }

                        return x;
                    });
                }

                data.Data.unshift([fromMoment.valueOf(), null]);
                data.Data.push([toMoment.valueOf(), null]);
            }

            seriesOptions.push({
                id: ParameterGroupInfoSet.Visibility_m.Id,
                name: `Visiblity (${MainSite.getUnitConverter().getConvertedUnits(ParameterGroupInfoSet.Visibility_m.Units, ParameterGroupInfoSet.Visibility_m.Id)})`,
                data: data.Data,
                lineWidth: 3,
                marker: {
                    enabled: false
                },
                yAxis: 1,
                color: '#FF0000',
                tooltip: {
                    valueDecimals: (unitsConversionSet && unitsConversionSet.convertedUnits == 'mi') ? 3 : 0
                }
            })

            if (seriesOptions.length == 4) {
                if (anyData) {
                    this.replaceChartSeries(seriesOptions);
                } else {
                    this.chart.showLoading('Data not available');
                }
            }
        });
    }

    private replaceChartSeries(seriesOptions: __Highcharts.SeriesOptions[]) {
        this.chart.hideLoading();
        if (this.chart.series.length < 1) {
            seriesOptions.forEach((series) => {
                this.chart.addSeries(series);
            });
        } else {
            seriesOptions.forEach((series) => {
                var cs: __Highcharts.SeriesObject = <__Highcharts.SeriesObject >this.chart.get(series.id);
                cs.setData(<any>series.data);
            });
        }

        this.chart.reflow();
    }
}